import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Card from "../Card";
import {
  addRoute,
  getRoute,
  updateRoute,
} from "../../services/OrganisationServices/transportManagemetService";
import {
  callToast,
  showErrorMessages,
} from "../../services/utilityFunctions.js";
import { useGlobalState } from "../../services/hostelConst";

const AddRouteForm = ({ refresh, navigate }) => {
  const { closeModal, masterTableState, updateMasterTable, modalState } =
    useGlobalState();
  const id = modalState.id;

  const [loadingData, setLoadingData] = useState(modalState.isEditing);

  const prepareFormData = (data = null) => {
    return {
      name: data == null ? "" : data.NAME,
      description: data == null ? "" : data.DESCRIPTION,
      startLocation: data == null ? "" : data.START_LOCATION,
      endLocation: data == null ? "" : data.END_LOCATION,
      distance: data == null ? "" : data.DISTANCE,
      estimatedTime: data == null ? "" : data.ESTIMATED_TIME,
    };
  };

  const [formData, setFormData] = useState(prepareFormData());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const getRouteDetailsData = async (id) => {
    setLoadingData(true);
    var res = await getRoute(id);
    if (res.status == "success") {
      setFormData(prepareFormData(res.data));
    } else {
      showErrorMessages({ data: res, navigate });
      closeModal();
    }
    setLoadingData(false);
  };

  useEffect(() => {
    if (modalState.isEditing && id) {
      getRouteDetailsData(id);
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Route name is required";
    }
    if (!formData.startLocation.trim()) {
      newErrors.startLocation = "Start location is required";
    }
    if (!formData.endLocation.trim()) {
      newErrors.endLocation = "End location is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        description: formData.description,
        startLocation: formData.startLocation,
        endLocation: formData.endLocation,
        distance: formData.distance,
        estimatedTime: formData.estimatedTime,
      };

      let res;
      if (modalState.isEditing) {
        submitData.entityId = id;
        res = await updateRoute(submitData);
      } else {
        res = await addRoute(submitData);
      }

      if (res.status === "success") {
        callToast(
          "success",
          "Success",
          `Route ${modalState.isEditing ? "updated" : "added"} successfully`
        );
        updateMasterTable(true);
        closeModal();
      } else {
        showErrorMessages({ data: res, navigate });
      }
    } catch (error) {
      console.error("Error submitting route:", error);
    }
    setLoading(false);
  };

  if (loadingData) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Route Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  isInvalid={!!errors.name}
                  placeholder="Enter route name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Location *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.startLocation}
                  onChange={(e) =>
                    handleChange("startLocation", e.target.value)
                  }
                  isInvalid={!!errors.startLocation}
                  placeholder="Enter start location"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.startLocation}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End Location *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.endLocation}
                  onChange={(e) => handleChange("endLocation", e.target.value)}
                  isInvalid={!!errors.endLocation}
                  placeholder="Enter end location"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.endLocation}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Distance (km)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={formData.distance}
                  onChange={(e) => handleChange("distance", e.target.value)}
                  placeholder="Enter distance in km"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Estimated Time (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.estimatedTime}
                  onChange={(e) =>
                    handleChange("estimatedTime", e.target.value)
                  }
                  placeholder="Enter estimated time in minutes"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Enter route description"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {modalState.isEditing ? "Updating..." : "Adding..."}
                </>
              ) : modalState.isEditing ? (
                "Update Route"
              ) : (
                "Add Route"
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddRouteForm;
